import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Wrench,
  Sparkles,
  Zap,
  Trees,
  Lock,
  Building2,
  Star,
  ArrowRight,
  Shield,
  Home,
  CheckCircle2,
} from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import apiService from '@/services/api';

const HomeownersPage = () => {
  const [, setSections] = useState<any>({});

  useEffect(() => {
    const fetchPageSections = async () => {
      try {
        const response = await apiService.get('/public/page-sections/homeowners');
        if (response.success && Array.isArray(response.data)) {
          const sectionMap: Record<string, any> = {};
          response.data.forEach((section: any) => {
            try {
              sectionMap[section.section_key] = {
                ...section,
                body: section.body ? JSON.parse(section.body) : section.body,
              };
            } catch (e) {
              sectionMap[section.section_key] = section;
            }
          });
          setSections(sectionMap);
        }
      } catch (error) {
        console.error('Error fetching page sections:', error);
      }
    };
    fetchPageSections();
  }, []);

  const services = [
    {
      icon: Wrench,
      title: 'Master Plumbing',
      description: 'From urgent leak repairs to full system overhauls, our certified plumbers ensure your water systems run perfectly.',
    },
    {
      icon: Sparkles,
      title: 'Elite Housekeeping',
      description: 'Meticulous attention to detail in every room. Your schedule and specific material requirements taken to heart.',
    },
    {
      icon: Zap,
      title: 'Precision Electrical',
      description: 'Safe, code-compliant electrical maintenance performed by certified technicians with years of expertise.',
    },
    {
      icon: Trees,
      title: 'Landscape Curation',
      description: 'Seasonal landscaping and garden maintenance that keeps your outdoor spaces looking pristine and manicured.',
    },
    {
      icon: Lock,
      title: 'Security & Access',
      description: 'Comprehensive security solutions, from lock systems to smart home integration with full compliance oversight.',
    },
    {
      icon: Building2,
      title: 'Project Management',
      description: 'Coordinating multi-discipline projects with precision timing, budgeting, and stakeholder communication.',
    },
  ];

  const testimonials = [
    {
      quote:
        'The level of professionalism is unmatchable. I no longer have to worry about quality. Virewire has become my silent partner in home management.',
      author: 'Margaret Stone',
      role: 'Property Owner, Kensington',
    },
    {
      quote:
        'Virewire. The concierge is always on speed dial, and the service team are the most respectful I have ever had in my home.',
      author: 'Elizabeth Watson',
      role: 'Private Residence Owner',
    },
  ];

  const stats = [
    { value: '5,000+', label: 'Properties Managed' },
    { value: '98%', label: 'Client Satisfaction' },
    { value: '24/7', label: 'Priority Response' },
    { value: '15 Years', label: 'Operational Excellence' },
  ];

  return (
    <Layout>
      <Helmet>
        <title>For Homeowners | Virewire Managed Services</title>
        <meta
          name="description"
          content="Professional property services for homeowners. No contractor hassle - we manage cleaning, maintenance, and repairs. Fixed prices, quality guarantee, dedicated support."
        />
        <link rel="canonical" href="https://virewire.co.uk/for/owners" />
      </Helmet>

      <div className="bg-white overflow-hidden">
        {/* ===== HERO SECTION ===== */}
        <section className="relative isolate overflow-hidden bg-gradient-to-br from-slate-950 via-teal-950 to-teal-900 text-white">
          <div className="absolute inset-0 -z-10">
            <div className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-teal-500/20 blur-3xl" />
            <div className="absolute -bottom-40 -right-20 h-[36rem] w-[36rem] rounded-full bg-emerald-400/15 blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
              backgroundSize: '56px 56px',
            }}
          />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide text-teal-100 backdrop-blur-md mb-6">
                  <Home className="h-3.5 w-3.5 text-emerald-300" />
                  For Homeowners
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] tracking-tight">
                  Your Home, Professionally
                  <span className="block bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 bg-clip-text text-transparent italic">
                    Serviced.
                  </span>
                  <span className="block">No Contractor Hassle.</span>
                </h1>

                <p className="text-lg text-teal-100/90 mb-8 leading-relaxed max-w-xl">
                  Virewire streamlines the management of your property. From urgent repairs to routine upkeep, we handle the logistics so you can enjoy peace of mind.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button className="group bg-white text-teal-800 hover:bg-teal-50 font-semibold px-8 h-12 text-base shadow-xl shadow-teal-950/30" asChild>
                    <Link to="/request-service">
                      Book a Consultation
                      <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:border-white/50 font-semibold px-8 h-12 text-base backdrop-blur-md"
                    asChild
                  >
                    <Link to="/how-it-works">See How It Works</Link>
                  </Button>
                </div>
              </div>

              {/* Visual composition */}
              <div className="relative h-[26rem] lg:h-[28rem]">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-teal-700/60 via-teal-800/60 to-emerald-900/60 backdrop-blur-md border border-white/10" />
                <div className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
                <div className="absolute -bottom-10 -left-10 h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />

                {/* Floating badges */}
                <div className="absolute top-10 left-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-3 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-emerald-400/30 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Verified pros</p>
                      <p className="text-teal-100 text-xs">Background checked</p>
                    </div>
                  </div>
                </div>

                <div className="absolute top-10 right-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-3 py-2 shadow-xl">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-3 w-3 fill-amber-300 text-amber-300" />
                    ))}
                    <span className="ml-1 text-white text-xs font-semibold">4.9</span>
                  </div>
                </div>

                <div className="absolute bottom-12 left-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-3 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-cyan-400/30 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">98% satisfaction</p>
                      <p className="text-teal-100 text-xs">guaranteed outcomes</p>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-12 right-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-3 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-teal-400/30 flex items-center justify-center">
                      <Lock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Insured & secure</p>
                      <p className="text-teal-100 text-xs">Protected every job</p>
                    </div>
                  </div>
                </div>

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 -m-8 rounded-full bg-white/5 animate-pulse" />
                    <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-2xl">
                      <Home className="w-12 h-12 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <svg
            className="absolute bottom-0 left-0 right-0 h-16 w-full text-white"
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,32 C240,80 480,80 720,48 C960,16 1200,16 1440,40 L1440,80 L0,80 Z" />
          </svg>
        </section>

        {/* ===== STATS BAR ===== */}
        <section className="relative -mt-12 px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-md p-6 sm:p-8 shadow-xl shadow-teal-900/5">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-br from-teal-700 to-emerald-600 bg-clip-text text-transparent">
                    {s.value}
                  </p>
                  <p className="text-gray-600 text-xs sm:text-sm mt-2 uppercase tracking-wide font-medium">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== COMPREHENSIVE HOME CARE ===== */}
        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-14 max-w-3xl">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Our Expertise</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                Comprehensive Home Care for the Discerning Owner
              </h2>
              <p className="text-lg text-gray-600">
                A single point of contact for every requirement. Orchestrated with uncompromising standards.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <Link
                    to="/services"
                    key={service.title}
                    className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-900/10 hover:border-teal-200"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative">
                      <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 ring-1 ring-teal-100 group-hover:from-teal-100 group-hover:to-emerald-100 transition-colors">
                        <Icon className="text-teal-700" size={26} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-5">{service.description}</p>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 group-hover:text-teal-800">
                        Explore Service
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== THREE STEPS TO SOVEREIGNTY ===== */}
        <section className="relative bg-gradient-to-b from-gray-50 to-white py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">The Process</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                Three Steps to Sovereignty
              </h2>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Connector line */}
              <div className="absolute left-0 right-0 top-10 hidden md:block">
                <div className="mx-32 h-px bg-gradient-to-r from-transparent via-teal-200 to-transparent" />
              </div>

              {[
                {
                  num: '01',
                  title: 'Initial Assessment',
                  desc: "A thorough audit of your property's status and your specific requirements to understand what matters most.",
                },
                {
                  num: '02',
                  title: 'Bespoke Roadmap',
                  desc: 'Creation of a tailored maintenance and service roadmap built around your schedule and priorities.',
                },
                {
                  num: '03',
                  title: 'Managed Execution',
                  desc: 'Sit back as our entire team orchestrates every service with transparency and total accountability.',
                },
              ].map((step) => (
                <div key={step.num} className="relative text-center">
                  <div className="relative mx-auto mb-5 h-20 w-20">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 opacity-90 shadow-lg shadow-teal-500/30" />
                    <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-2xl tracking-tight">
                      {step.num}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== TESTIMONIALS ===== */}
        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Visual composition */}
              <div className="relative h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-teal-700 via-teal-800 to-emerald-900 shadow-2xl shadow-teal-900/30">
                <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-emerald-400/30 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage:
                      'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                  }}
                />

                <div className="absolute top-10 left-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-3 shadow-xl">
                  <div className="flex items-center gap-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-amber-300 text-amber-300" />
                    ))}
                  </div>
                  <p className="text-white text-sm font-semibold mt-1">4.9 from 2,400+ owners</p>
                </div>

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 -m-8 rounded-full bg-white/5 animate-pulse" />
                    <div className="relative h-28 w-28 rounded-full bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-2xl">
                      <Home className="w-14 h-14 text-white" />
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-10 right-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-3 shadow-xl">
                  <p className="text-emerald-200 text-[10px] font-bold tracking-widest mb-1">SAVED THIS YEAR</p>
                  <p className="text-white text-2xl font-bold">142+ hours</p>
                </div>
              </div>

              <div className="space-y-8">
                <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs">Testimonials</p>
                {testimonials.map((testimonial, idx) => (
                  <div key={idx} className="relative pl-6 border-l-2 border-teal-200">
                    <svg
                      className="absolute -top-2 -left-3 h-7 w-7 text-teal-200"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M9.5,7C7,7 5,9 5,11.5V17H10.5V11.5H7.5C7.5,10.1 8.6,9 10,9V7H9.5M16.5,7C14,7 12,9 12,11.5V17H17.5V11.5H14.5C14.5,10.1 15.6,9 17,9V7H16.5Z" />
                    </svg>
                    <p className="text-lg sm:text-xl text-gray-900 mb-4 leading-relaxed">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white font-bold shadow-md shadow-teal-500/20">
                        {testimonial.author.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{testimonial.author}</p>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="px-4 sm:px-6 lg:px-8 pb-24">
          <div className="relative max-w-5xl mx-auto rounded-[2rem] overflow-hidden shadow-2xl shadow-teal-900/20">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700" />
            <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />
            <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" preserveAspectRatio="none">
              <defs>
                <pattern id="diagonal-stripe-ho" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                  <line x1="0" y1="0" x2="0" y2="20" stroke="white" strokeWidth="10" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#diagonal-stripe-ho)" />
            </svg>

            <div className="relative z-10 max-w-4xl mx-auto text-center px-8 py-20 lg:py-24 text-white">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide backdrop-blur-md mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                Trusted by 5,000+ homeowners
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 tracking-tight">
                Ready to Restore Order to Your Home?
              </h2>
              <p className="text-lg text-teal-50 mb-10 max-w-2xl mx-auto leading-relaxed">
                Join thousands of homeowners who have delegated their property stress to the professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button className="group bg-white text-teal-800 hover:bg-teal-50 font-semibold px-8 h-12 text-base shadow-xl" asChild>
                  <Link to="/request-service">
                    Request a Service
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="border-white/40 bg-white/5 text-white hover:bg-white/15 hover:border-white/60 font-semibold px-8 h-12 text-base backdrop-blur-md"
                  asChild
                >
                  <Link to="/contact">Speak with Our Team</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomeownersPage;
